import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCompliances } from "@/api/compliances";
import { getTerms, getBoldSignTemplates, syncBoldSignTemplates, updateBoldSignTemplate } from "@/api/terms";
import { getSkills } from "@/api/skills";
import ComplianceForm from "@/pages/admin/terms/ComplianceForm";
import TermForm from "@/pages/admin/terms/TermForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Plus, RefreshCw } from "lucide-react";

export default function TermsIndex() {
    const navigate = useNavigate();
    const [compliances, setCompliances] = useState([]);
    const [terms, setTerms] = useState([]);
    const [boldSignTemplates, setBoldSignTemplates] = useState([]);
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState(null); // 'term' or 'compliance'
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const [complianceData, termsData, boldSignData, skillsData] = await Promise.all([
            getCompliances(),
            getTerms(),
            getBoldSignTemplates(),
            getSkills(),
        ]);
        if (complianceData) setCompliances(complianceData);
        if (termsData) setTerms(termsData);
        if (boldSignData) setBoldSignTemplates(boldSignData);
        if (skillsData) setSkills(skillsData);
        setLoading(false);
    };

    const openDialog = (type, item = null) => {
        setModalType(type);
        setSelectedItem(item);
        setModalOpen(true);
    };

    const handleSave = () => {
        setModalOpen(false);
        setSelectedItem(null);
        loadData();
    };

    const handleSync = async () => {
        setSyncing(true);
        console.log('Starting BoldSign sync...');
        try {
            const result = await syncBoldSignTemplates();
            console.log('Sync result:', result);
            await loadData();
            console.log('Data reloaded after sync');
        } catch (error) {
            console.error('Error syncing BoldSign templates:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
        } finally {
            setSyncing(false);
            console.log('Sync process completed');
        }
    };

    const handleSkillAssignment = async (templateId, skillId) => {
        console.log('Updating template skill:', { templateId, skillId });
        try {
            const result = await updateBoldSignTemplate(templateId, { skill_id: skillId });
            console.log('Update result:', result);
            await loadData();
            console.log('Data reloaded after skill assignment');
        } catch (error) {
            console.error('Error updating template skill:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
        }
    };

    const handleActiveCandidateToggle = async (templateId, isActive) => {
        console.log('Updating template active_for_candidate status:', { templateId, isActive });
        try {
            const result = await updateBoldSignTemplate(templateId, { active_for_candidate: isActive });
            console.log('Update result:', result);
            await loadData();
            console.log('Data reloaded after active status change');
        } catch (error) {
            console.error('Error updating template active_for_candidate status:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
        }
    };

    const handleActiveClientToggle = async (templateId, isActive) => {
        console.log('Updating template active_for_client status:', { templateId, isActive });
        try {
            const result = await updateBoldSignTemplate(templateId, { active_for_client: isActive });
            console.log('Update result:', result);
            await loadData();
            console.log('Data reloaded after active status change');
        } catch (error) {
            console.error('Error updating template active_for_client status:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
        }
    };

    return (
        <div className="space-y-10 p-6">
            {/* Compliance Documents */}
            <section>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold text-[#5D1F42]">Compliance Documents</h2>
                    <button
                        onClick={() => openDialog("compliance")}
                        className="flex items-center gap-2 bg-[#5D1F42] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#712a55] transition"
                    >
                        <Plus className="w-4 h-4" />
                        Add New
                    </button>
                </div>

                <div className="bg-white shadow rounded-xl overflow-auto">
                    <table className="min-w-full border border-gray-200 text-sm">
                        <thead className="bg-gray-50 text-left text-gray-700 uppercase text-xs tracking-wider">
                        <tr>
                            <th className="px-4 py-3 border-b">Name</th>
                            <th className="px-4 py-3 border-b text-right">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        {compliances.map((doc) => (
                            <tr key={doc.id} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-2">{doc.name}</td>
                                <td className="px-4 py-2 text-right">
                                    <button
                                        onClick={() => openDialog("compliance", doc)}
                                        className="text-sm text-[#5D1F42] hover:underline inline-flex items-center gap-1"
                                    >
                                        <Pencil className="w-4 h-4" />
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Terms & Agreements - Hidden for now */}
            {/*
            <section>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold text-[#5D1F42]">Terms & Agreements</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={() => openDialog("term")}
                            className="flex items-center gap-2 bg-[#5D1F42] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#712a55] transition"
                        >
                            <Plus className="w-4 h-4" />
                            Add New
                        </button>
                    </div>
                </div>

                <div className="bg-white shadow rounded-xl overflow-auto">
                    <table className="min-w-full border border-gray-200 text-sm">
                        <thead className="bg-gray-50 text-left text-gray-700 uppercase text-xs tracking-wider">
                        <tr>
                            <th className="px-4 py-3 border-b">Name</th>
                            <th className="px-4 py-3 border-b">Skill</th>
                            <th className="px-4 py-3 border-b">Type</th>
                            <th className="px-4 py-3 border-b">Copy Preview</th>
                            <th className="px-4 py-3 border-b">File</th>
                            <th className="px-4 py-3 border-b text-right">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        {terms.map((term) => (
                            <tr key={term.id} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-2">{term.name}</td>
                                <td className="px-4 py-2">
                                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                        {term.skillName || 'Default'}
                                    </span>
                                </td>
                                <td className="px-4 py-2">{term.termType == 'text' ? "Text" : "File"}</td>
                                <td className="px-4 py-2 text-gray-600 italic">
                                    {term.termType === 'text' ? (
                                        <span className="line-clamp-2">{term.copy}</span>
                                    ) : (
                                        "—"
                                    )}
                                </td>
                                <td className="px-4 py-2">
                                    {term.termType === 'file' && term.fileUrl ? (
                                        <a
                                            href={term.fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#5D1F42] hover:underline text-sm"
                                        >
                                            Download
                                        </a>
                                    ) : (
                                        "—"
                                    )}
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <button
                                        onClick={() => openDialog("term", term)}
                                        className="text-sm text-[#5D1F42] hover:underline inline-flex items-center gap-1"
                                    >
                                        <Pencil className="w-4 h-4" />
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </section>
            */}

            {/* BoldSign Documents */}
            <section>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold text-[#5D1F42]">BoldSign Documents</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={handleSync}
                            disabled={syncing}
                            className="flex items-center gap-2 bg-[#5D1F42] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#712a55] transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                            {syncing ? 'Syncing...' : 'Sync from BoldSign'}
                        </button>
                    </div>
                </div>

                <div className="bg-white shadow rounded-xl overflow-auto">
                    <table className="min-w-full border border-gray-200 text-sm">
                        <thead className="bg-gray-50 text-left text-gray-700 uppercase text-xs tracking-wider">
                        <tr>
                            <th className="px-4 py-3 border-b">Template Name</th>
                            <th className="px-4 py-3 border-b">Job Title (Candidate Only)</th>
                            <th className="px-4 py-3 border-b">Candidate</th>
                            <th className="px-4 py-3 border-b">Client</th>
                            <th className="px-4 py-3 border-b">BoldSign ID</th>
                            <th className="px-4 py-3 border-b">Created Date</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        {boldSignTemplates.map((template) => (
                            <tr key={template.id} className="hover:bg-gray-50 transition">
                                <td className="px-4 py-2">{template.templateName || '—'}</td>
                                <td className="px-4 py-2">
                                    <select
                                        value={template.skillId || ''}
                                        onChange={(e) => handleSkillAssignment(template.id, e.target.value || null)}
                                        className="text-xs bg-gray-100 px-2 py-1 rounded border-none outline-none"
                                    >
                                        <option value="">Default (all candidates)</option>
                                        {skills.map((skill) => (
                                            <option key={skill.id} value={skill.id}>
                                                {skill.name}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="px-4 py-2">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={template.activeForCandidate || false}
                                            onChange={(e) => handleActiveCandidateToggle(template.id, e.target.checked)}
                                            className="form-checkbox h-4 w-4 text-[#5D1F42] rounded border-gray-300 focus:ring-[#5D1F42] focus:ring-2"
                                        />
                                        <span className="ml-2 text-xs text-gray-600">
                                            {template.activeForCandidate ? 'Active' : 'Inactive'}
                                        </span>
                                    </label>
                                </td>
                                <td className="px-4 py-2">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={template.activeForClient || false}
                                            onChange={(e) => handleActiveClientToggle(template.id, e.target.checked)}
                                            className="form-checkbox h-4 w-4 text-[#5D1F42] rounded border-gray-300 focus:ring-[#5D1F42] focus:ring-2"
                                        />
                                        <span className="ml-2 text-xs text-gray-600">
                                            {template.activeForClient ? 'Active' : 'Inactive'}
                                        </span>
                                    </label>
                                </td>
                                <td className="px-4 py-2">
                                    <span className="text-xs bg-blue-100 px-2 py-1 rounded font-mono">
                                        {template.boldSignId || '—'}
                                    </span>
                                </td>
                                <td className="px-4 py-2 text-gray-600">
                                    {template.createdAt ? new Date(template.createdAt).toLocaleDateString() : '—'}
                                </td>
                            </tr>
                        ))}
                        {boldSignTemplates.length === 0 && (
                            <tr>
                                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                    No BoldSign templates found
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Shared Modal for Both Forms */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedItem ? `Edit ${modalType}` : `Add New ${modalType}`}
                        </DialogTitle>
                    </DialogHeader>

                    {modalType === "compliance" && (
                        <ComplianceForm compliance={selectedItem} onSave={handleSave} />
                    )}

                    {modalType === "term" && (
                        <TermForm term={selectedItem} onSave={handleSave} />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
